/**
 * オブジェクト処理ユーティリティークラス
 */
class ObjectUtil {
  /**
   * フラット化
   * @param {Object} obj オブジェクト
   * @returns {Object} フラット化したオブジェクト(キーは.区切り)
   */
  flat(obj) {
    const result = {}
    for (const [key, value] of Object.entries(obj)) {
      if (
        typeof value === 'object' &&
        value !== null &&
        value.constructor === Object &&
        Object.keys(value).length > 0
      ) {
        const childObject = this.flat(value)
        for (const [childKey, childValue] of Object.entries(childObject)) {
          result[`${key}.${childKey}`] = childValue
        }
      } else {
        result[key] = value
      }
    }
    return result
  }

  /**
   * プロトタイプ汚染ガード付きの深いマージ（source を優先）。lodash の _.merge の代替。
   * __proto__ / constructor / prototype キーは無視する（プロトタイプ汚染対策）。
   * @param {Object} target マージ先（破壊的に変更される）
   * @param {Object} source マージ元（同一キーは source 優先）
   * @returns {Object} マージ後の target
   */
  deepMerge(target, source) {
    if (!this.#isPlainObject(target) || !this.#isPlainObject(source)) {
      return target
    }
    for (const key of Object.keys(source)) {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        continue
      }
      const sourceValue = source[key]
      const targetValue = target[key]
      if (
        this.#isPlainObject(sourceValue) &&
        this.#isPlainObject(targetValue)
      ) {
        this.deepMerge(targetValue, sourceValue)
      } else if (this.#isPlainObject(sourceValue)) {
        target[key] = this.deepMerge({}, sourceValue)
      } else {
        target[key] = sourceValue
      }
    }
    return target
  }

  /**
   * プロトタイプ汚染ガード付きの深いデフォルト適用（target を優先）。lodash の _.defaultsDeep の代替。
   * target に存在しない（undefined）キーのみ source の値で深く補完する。
   * __proto__ / constructor / prototype キーは無視する（プロトタイプ汚染対策）。
   * @param {Object} target 対象（破壊的に変更される。既存値を優先）
   * @param {Object} source デフォルト値の供給元
   * @returns {Object} 補完後の target
   */
  deepDefaults(target, source) {
    if (!this.#isPlainObject(target) || !this.#isPlainObject(source)) {
      return target
    }
    for (const key of Object.keys(source)) {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        continue
      }
      const sourceValue = source[key]
      const targetValue = target[key]
      if (
        this.#isPlainObject(sourceValue) &&
        this.#isPlainObject(targetValue)
      ) {
        this.deepDefaults(targetValue, sourceValue)
      } else if (targetValue === undefined) {
        target[key] = this.#isPlainObject(sourceValue)
          ? this.deepDefaults({}, sourceValue)
          : sourceValue
      }
    }
    return target
  }

  /**
   * プレーンオブジェクト判定
   * @param {*} value 判定対象
   * @returns {boolean} プレーンオブジェクトなら true
   */
  #isPlainObject(value) {
    return (
      typeof value === 'object' &&
      value !== null &&
      value.constructor === Object
    )
  }
}

export default new ObjectUtil()
